import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import AuthPhoneField from "../../../customComponents/AuthPhoneField";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage, alertWarningMessage } from "../../../customComponents/CustomAlertMessage";
import { ProfileContext } from "../../../context/ProfileProvider";
import { countriesList } from "../../../utils/CountriesList";
import "../WithdrawPage/withdrawal.css";
import "./security.css";
import "./emergencyContact.css";

const EMERGENCY_CONTACT_MAX = 5;

const METHOD_PRIORITY = ["passkey", "totp", "email", "mobile"];

const CHOOSE_METHOD_ROW = {
  passkey: {
    title: "Passkey",
    description: "Sign in with Face ID, Touch ID, or your security key.",
    icon: "/images/passkey_vector.svg",
  },
  email: {
    title: "Email OTP",
    description: "Get a one-time code sent to your email",
    icon: "/images/email_vector2.svg",
  },
  mobile: {
    title: "Mobile OTP",
    description: "Receive a verification code via SMS",
    icon: "/images/mobileicon.svg",
  },
  totp: {
    title: "Authenticator App",
    description: "Enter the 6-digit code from your authenticator app",
    icon: "/images/authenticator_icon.svg",
  },
};

/** Pull `security_methods`-like object from GET `get-security-methods-list` response. */
function pickSecurityMethodsPayload(result) {
  if (!result || typeof result !== "object") return null;
  const d = result.data;
  if (d?.security_methods && typeof d.security_methods === "object") return d.security_methods;
  if (d?.data?.security_methods && typeof d.data.security_methods === "object") return d.data.security_methods;
  if (result.security_methods && typeof result.security_methods === "object") return result.security_methods;
  if (d?.securityMethods && typeof d.securityMethods === "object") return d.securityMethods;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    const looksLikeFlags =
      "totp" in d ||
      "email" in d ||
      "mobile" in d ||
      "phone" in d ||
      "sms" in d ||
      "googleAuth" in d ||
      "google_auth" in d ||
      "passkey" in d;
    if (looksLikeFlags) return d;
  }
  return null;
}

function truthySecurityFlag(v) {
  if (v === true || v === 1) return true;
  if (v == null || v === false || v === 0) return false;
  const s = String(v).toLowerCase();
  return s === "true" || s === "1" || s === "yes";
}

/** Normalize flags so UI matches whatever the API marks enabled. */
function normalizeSecurityMethodsForEmergency(m) {
  if (!m || typeof m !== "object") return { totp: false, email: false, mobile: false, passkey: false };
  const anyTrue = (...vals) => vals.some((v) => truthySecurityFlag(v));
  return {
    passkey: anyTrue(m.passkey, m.webauthn, m.WebAuthn),
    totp: anyTrue(
      m.totp,
      m.TOTP,
      m.google_auth,
      m.googleAuth,
      m.authenticator,
      m.google_authenticator,
      m.googleAuthenticator
    ),
    email: anyTrue(m.email, m.Email),
    mobile: anyTrue(m.mobile, m.phone, m.sms, m.Mobile, m.Phone, m.SMS),
  };
}

function pickPreferredVerificationMethod(methods) {
  if (!methods) return null;
  const hit = METHOD_PRIORITY.find((k) => methods[k]);
  return hit || null;
}

function maskPhoneDigits(digits) {
  const d = String(digits || "").replace(/\D/g, "");
  if (d.length < 4) return "your phone";
  return `***${d.slice(-4)}`;
}

function ChooseVerificationMethodModal({ methods, onClose, onPick, onSecurityUnavailable }) {
  return (
    <div className="withdraw_confirm_overlay pm-choose-method-overlay-stacked" role="presentation">
      <div className="withdraw_choose_modal pm-choose-method-modal" role="dialog" aria-modal="true" aria-labelledby="ec-em-choose-title">
        <div className="withdraw_choose_topbar pm-choose-method-modal__topbar">
          <span className="pm-choose-method-modal__topbarSpacer" aria-hidden="true" />
          <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <h2 id="ec-em-choose-title" className="withdraw_choose_title pm-choose-method-modal__title">
          Choose Verification Method
        </h2>
        <p className="withdraw_choose_subtitle pm-choose-method-modal__subtitle">
          Select how you&apos;d like to confirm your identity
        </p>

        <div className="emailphone_outerbl pm-choose-method-modal__list">
          {methods.map((key) => {
            const row = CHOOSE_METHOD_ROW[key];
            if (!row) return null;
            return (
              <button key={key} type="button" className="withdraw_choose_item pm-choose-method-item" onClick={() => onPick(key)}>
                <img src={row.icon} alt="" aria-hidden="true" />
                <div className="pm-choose-method-item__text">
                  <span className="pm-choose-method-item__title">{row.title}</span>
                  <span className="pm-choose-method-item__desc">{row.description}</span>
                </div>
                <i className="ri-arrow-right-s-line pm-choose-method-item__chevron" aria-hidden="true" />
              </button>
            );
          })}
        </div>

        <div className="mid_use_text">
          <button type="button" className="withdraw_auth_link" onClick={() => onSecurityUnavailable?.()}>
            Security verification unavailable?
          </button>
        </div>

        <div className="withdraw_passkey_footer withdraw_auth_footer passkey_footer_text">
          <div className="withdraw_passkey_footer__inner">
            <img src="/images/protected_icon.svg" alt="shield icon" />
            <span>Protected by Balance Risk</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Best-effort split of stored `mobileNumber` into dial code + national digits for `AuthPhoneField`.
 */
function splitInternationalMobile(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return { countryCode: "+91", phoneDigits: "" };
  const digitsOnly = s.replace(/\D/g, "");
  const sorted = [...countriesList].sort(
    (a, b) => String(b.value).replace(/\D/g, "").length - String(a.value).replace(/\D/g, "").length
  );
  for (const c of sorted) {
    const dialDigits = String(c.value).replace(/\D/g, "");
    if (dialDigits && digitsOnly.startsWith(dialDigits)) {
      return {
        countryCode: c.value,
        phoneDigits: digitsOnly.slice(dialDigits.length),
      };
    }
  }
  return { countryCode: "+91", phoneDigits: digitsOnly };
}

function buildMobileNumber(countryCode, phoneClean) {
  const cc = String(countryCode || "").trim();
  const digits = String(phoneClean || "").replace(/\D/g, "");
  return `${cc}${digits}`;
}

/**
 * Emergency Contact — standalone page (scoped CSS: ec-page).
 * Route: /user_profile/security/emergencyContact
 * API: /v1/security/* (add / edit / delete / list / save message)
 */
const EmergencyContact = () => {
  const navigate = useNavigate();
  const profileCtx = useContext(ProfileContext) || {};
  const userDetails = profileCtx.userDetails || {};

  const accountEmail = String(userDetails?.emailId || userDetails?.email || "").trim();
  const accountPhoneDigits = String(userDetails?.mobileNumber || userDetails?.phone || "").replace(/\D/g, "");

  const pendingFlowRef = useRef(null);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);

  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [phoneDigits, setPhoneDigits] = useState("");

  const [contacts, setContacts] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [messageDrafts, setMessageDrafts] = useState({});
  const [saveMsgBusyId, setSaveMsgBusyId] = useState(null);
  const [deleteBusyId, setDeleteBusyId] = useState(null);

  /** Normalized enabled methods for chooser + alternate list */
  const [resolvedMethods, setResolvedMethods] = useState(null);
  /** null | "passkey" | "totp" | "email" | "mobile" */
  const [verifyModal, setVerifyModal] = useState(null);
  const [chooseMethodsOpen, setChooseMethodsOpen] = useState(false);
  const [flowBusy, setFlowBusy] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyAlternateEligible, setPasskeyAlternateEligible] = useState(false);

  const emailTrim = contactEmail.trim();
  const nameTrim = contactName.trim();
  const phoneClean = phoneDigits.replace(/\D/g, "");
  const hasEmail = emailTrim.length > 0 && emailTrim.includes("@");
  const hasPhone = phoneClean.length >= 8;
  const canSave = useMemo(() => nameTrim.length > 0 && hasEmail && hasPhone, [nameTrim, hasEmail, hasPhone]);

  const phoneDisplay = useMemo(() => {
    if (!hasPhone) return "";
    const cc = String(countryCode || "").trim();
    return cc ? `${cc} ${phoneClean}` : phoneClean;
  }, [hasPhone, countryCode, phoneClean]);

  const maskedEmail = useMemo(() => {
    const email = accountEmail;
    if (!email || !email.includes("@")) return "your email";
    const [name, domain] = email.split("@");
    return `${name.slice(0, 2)}***@${domain}`;
  }, [accountEmail]);

  const maskedPhone = useMemo(() => maskPhoneDigits(accountPhoneDigits), [accountPhoneDigits]);

  const fetchContacts = useCallback(async () => {
    setListLoading(true);
    try {
      const result = await AuthService.getEmergencyContactList();
      if (!result?.success) {
        alertErrorMessage(result?.message || "Could not load emergency contacts.");
        setContacts([]);
        setMessageDrafts({});
        return;
      }
      const raw = result?.data;
      const list = Array.isArray(raw) ? raw : [];
      setContacts(list);
      const drafts = {};
      list.forEach((c) => {
        drafts[String(c._id)] = c.message != null ? String(c.message) : "";
      });
      setMessageDrafts(drafts);
    } catch (e) {
      alertErrorMessage(e?.message || "Could not load emergency contacts.");
      setContacts([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchContacts();
  }, [fetchContacts]);

  const resetVerificationUi = useCallback(() => {
    setResolvedMethods(null);
    setVerifyModal(null);
    setChooseMethodsOpen(false);
    setFlowBusy(false);
    setOtpSending(false);
    setOtpCode("");
    setTotpCode("");
    setPasskeyBusy(false);
    setPasskeyAlternateEligible(false);
  }, []);

  const resetAll = useCallback(() => {
    setAddModalOpen(false);
    setEditingContactId(null);
    setContactName("");
    setContactEmail("");
    setCountryCode("+91");
    setPhoneDigits("");
    resetVerificationUi();
    pendingFlowRef.current = null;
  }, [resetVerificationUi]);

  const closeAddModal = useCallback(() => {
    setAddModalOpen(false);
    setEditingContactId(null);
    setContactName("");
    setContactEmail("");
    setCountryCode("+91");
    setPhoneDigits("");
    resetVerificationUi();
    pendingFlowRef.current = null;
  }, [resetVerificationUi]);

  const goSecurityUnavailable = () => navigate("/asset_managemnet/security_verification_unavailable");

  const fetchResolvedEmergencyMethods = useCallback(async () => {
    const norm =
      typeof profileCtx.fetchSecurityMethods === "function" ? await profileCtx.fetchSecurityMethods() : null;

    if (norm) {
      return {
        passkey: !!norm.passkey,
        totp: !!norm.totp,
        email: !!norm.email,
        mobile: !!norm.mobile,
      };
    }

    const result = await AuthService.getSecurityMethodsList();
    const payload = pickSecurityMethodsPayload(result);
    if (!result?.success || !payload) return null;
    return normalizeSecurityMethodsForEmergency(payload);
  }, [profileCtx]);

  /** Dispatches verified POST for add / edit / delete / save-message (`mode` matches SecurityController). */
  const completeEmergencyActionAfterVerification = useCallback(
    async ({ type, code = "", credential }) => {
      const flow = pendingFlowRef.current;
      if (!flow) {
        alertErrorMessage("Session expired. Please try again.");
        return false;
      }
      const { mode, payload } = flow;
      LoaderHelper.loaderStatus(true);
      try {
        let res;
        if (mode === "add") {
          res = await AuthService.addEmergencyContact({
            fullName: payload.fullName,
            emailId: payload.emailId,
            mobileNumber: payload.mobileNumber,
            type,
            code,
            credential,
          });
        } else if (mode === "edit") {
          res = await AuthService.editEmergencyContact({
            contactId: payload.contactId,
            fullName: payload.fullName,
            emailId: payload.emailId,
            mobileNumber: payload.mobileNumber,
            type,
            code,
            credential,
          });
        } else if (mode === "delete") {
          res = await AuthService.deleteEmergencyContact({
            contactId: payload.contactId,
            type,
            code,
            credential,
          });
        } else if (mode === "saveMessage") {
          res = await AuthService.saveEmergencyContactMessage({
            contactId: payload.contactId,
            message: payload.message,
            type,
            code,
            credential,
          });
        } else {
          alertErrorMessage("Unknown action.");
          return false;
        }

        if (res?.success) {
          const defaults = {
            add: "Emergency contact added.",
            edit: "Emergency contact updated.",
            delete: "Emergency contact removed.",
            saveMessage: "Message saved.",
          };
          alertSuccessMessage(res.message || defaults[mode] || "Done.");
          pendingFlowRef.current = null;
          resetVerificationUi();
          setDeleteBusyId(null);
          setSaveMsgBusyId(null);

          if (mode === "add" || mode === "edit") {
            resetAll();
            void fetchContacts();
            navigate("/user_profile/security", { replace: true });
          } else {
            void fetchContacts();
            if (mode === "delete") {
              navigate("/user_profile/security", { replace: true });
            }
          }
          return true;
        }
        alertErrorMessage(res?.message || "Request failed.");
        if (mode === "delete" || mode === "saveMessage") {
          setDeleteBusyId(null);
          setSaveMsgBusyId(null);
        }
        return false;
      } catch (e) {
        alertErrorMessage(e?.message || "Request failed.");
        const failMode = pendingFlowRef.current?.mode;
        if (failMode === "delete" || failMode === "saveMessage") {
          setDeleteBusyId(null);
          setSaveMsgBusyId(null);
        }
        return false;
      } finally {
        LoaderHelper.loaderStatus(false);
      }
    },
    [resetAll, resetVerificationUi, fetchContacts, navigate]
  );

  const resetVerificationInputs = useCallback(() => {
    setOtpCode("");
    setTotpCode("");
  }, []);

  const closeVerifyFlow = useCallback(() => {
    const reopen = pendingFlowRef.current?.reopenAddModalOnCancel === true;
    resetVerificationUi();
    pendingFlowRef.current = null;
    setDeleteBusyId(null);
    setSaveMsgBusyId(null);
    if (reopen) setAddModalOpen(true);
  }, [resetVerificationUi]);

  /** @returns {Promise<"success"|"cancelled"|"failed"|"invalid">} */
  const verifyPasskeyEmergency = useCallback(
    async (opts = {}) => {
      const { silentCancel = false } = opts;
      const signId = accountEmail || accountPhoneDigits;
      if (!signId) {
        alertErrorMessage("Account identifier missing for passkey verification.");
        return "invalid";
      }
      setPasskeyAlternateEligible(false);
      setPasskeyBusy(true);
      LoaderHelper.loaderStatus(true);
      try {
        const optionsResult = await AuthService.passkeyGetAuthOptions(signId);
        if (!optionsResult?.success || !optionsResult?.data) {
          alertErrorMessage(optionsResult?.message || "Failed to get passkey options");
          setPasskeyAlternateEligible(true);
          return "failed";
        }
        let credential;
        LoaderHelper.loaderStatus(false);
        try {
          credential = await startAuthentication(optionsResult.data);
        } catch (webauthnError) {
          LoaderHelper.loaderStatus(false);
          if (webauthnError?.name === "NotAllowedError") {
            if (!silentCancel) {
              alertErrorMessage("Authentication was cancelled or timed out. Try again or use another method.");
            }
            setPasskeyAlternateEligible(true);
            return "cancelled";
          }
          alertErrorMessage("Passkey verification failed.");
          setPasskeyAlternateEligible(true);
          return "failed";
        }
        LoaderHelper.loaderStatus(true);
        const ok = await completeEmergencyActionAfterVerification({
          type: "passkey",
          code: "",
          credential,
        });
        if (ok) return "success";
        setPasskeyAlternateEligible(true);
        return "failed";
      } catch (e) {
        alertErrorMessage(e?.message || "Passkey verification failed");
        setPasskeyAlternateEligible(true);
        return "failed";
      } finally {
        setPasskeyBusy(false);
        LoaderHelper.loaderStatus(false);
      }
    },
    [accountEmail, accountPhoneDigits, completeEmergencyActionAfterVerification]
  );

  const alternateMethodKeys = useMemo(() => {
    if (!resolvedMethods || !verifyModal) return [];
    return METHOD_PRIORITY.filter((k) => resolvedMethods[k] && k !== verifyModal);
  }, [resolvedMethods, verifyModal]);

  const sendEmergencyOtp = useCallback(async (target) => {
    const security_methods = target === "mobile" ? "mobile" : "email";
    setOtpSending(true);
    try {
      const result = await AuthService.sendEmergencyContactOtp(security_methods);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent");
        return true;
      }
      alertErrorMessage(result?.message || "Failed to send verification code");
      return false;
    } catch (e) {
      alertErrorMessage(e?.message || "Failed to send verification code");
      return false;
    } finally {
      setOtpSending(false);
    }
  }, []);

  const openChooseMethods = useCallback(() => {
    if (alternateMethodKeys.length === 0) {
      alertErrorMessage("No other verification method is available.");
      return;
    }
    setChooseMethodsOpen(true);
  }, [alternateMethodKeys.length]);

  const applyVerificationMethod = useCallback(
    async (method) => {
      setChooseMethodsOpen(false);
      resetVerificationInputs();
      if (method === "passkey") {
        setPasskeyAlternateEligible(false);
        setVerifyModal(null);
        const outcome = await verifyPasskeyEmergency({ silentCancel: true });
        if (outcome === "invalid") {
          closeVerifyFlow();
          return;
        }
        if (outcome !== "success") {
          setVerifyModal("passkey");
        }
        return;
      }
      setVerifyModal(method);
    },
    [resetVerificationInputs, verifyPasskeyEmergency, closeVerifyFlow]
  );

  const runEmergencyVerificationSequence = useCallback(async () => {
    LoaderHelper.loaderStatus(true);
    let methods;
    try {
      methods = await fetchResolvedEmergencyMethods();
    } catch (e) {
      alertErrorMessage(e?.message || "Could not load verification methods.");
      const reopen = pendingFlowRef.current?.reopenAddModalOnCancel === true;
      pendingFlowRef.current = null;
      setDeleteBusyId(null);
      setSaveMsgBusyId(null);
      if (reopen) setAddModalOpen(true);
      LoaderHelper.loaderStatus(false);
      return;
    } finally {
      LoaderHelper.loaderStatus(false);
    }

    const preferred = methods ? pickPreferredVerificationMethod(methods) : null;
    if (!methods || !preferred) {
      alertErrorMessage(
        methods
          ? "No verification method available. Enable email, mobile, authenticator, or passkey in Security first."
          : "Could not load verification methods."
      );
      const reopen = pendingFlowRef.current?.reopenAddModalOnCancel === true;
      pendingFlowRef.current = null;
      setDeleteBusyId(null);
      setSaveMsgBusyId(null);
      if (reopen) setAddModalOpen(true);
      return;
    }

    setResolvedMethods(methods);
    resetVerificationInputs();

    setFlowBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      if (preferred === "passkey") {
        setChooseMethodsOpen(false);
        setPasskeyAlternateEligible(false);
        setVerifyModal(null);
        const outcome = await verifyPasskeyEmergency({ silentCancel: true });
        if (outcome === "invalid") {
          closeVerifyFlow();
          return;
        }
        if (outcome !== "success") {
          setVerifyModal("passkey");
        }
        return;
      }

      setVerifyModal(preferred);
    } catch (e) {
      alertErrorMessage(e?.message || "Something went wrong.");
    } finally {
      setFlowBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  }, [
    fetchResolvedEmergencyMethods,
    resetVerificationInputs,
    verifyPasskeyEmergency,
    closeVerifyFlow,
  ]);

  const openEmergencyVerificationFromSave = async () => {
    if (!canSave) return;
    if (!hasEmail || !hasPhone) {
      alertErrorMessage("Please enter full name, a valid email, and a phone number (required by the server).");
      return;
    }

    const mobileNumber = buildMobileNumber(countryCode, phoneClean);
    pendingFlowRef.current = editingContactId
      ? {
        mode: "edit",
        reopenAddModalOnCancel: true,
        payload: {
          contactId: editingContactId,
          fullName: nameTrim,
          emailId: emailTrim,
          mobileNumber,
        },
      }
      : {
        mode: "add",
        reopenAddModalOnCancel: true,
        payload: {
          fullName: nameTrim,
          emailId: emailTrim,
          mobileNumber,
        },
      };

    setAddModalOpen(false);
    resetVerificationUi();
    setResolvedMethods(null);

    await runEmergencyVerificationSequence();
  };

  const verifyTotpSubmit = async () => {
    const code = String(totpCode || "").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      alertErrorMessage("Enter the 6-digit authenticator code.");
      return;
    }
    setFlowBusy(true);
    try {
      await completeEmergencyActionAfterVerification({ type: "totp", code });
    } finally {
      setFlowBusy(false);
    }
  };

  const verifyEmailOrMobileSubmit = async () => {
    const code = String(otpCode || "").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) return;
    const apiType = verifyModal === "mobile" ? "phone" : "email";
    setFlowBusy(true);
    try {
      await completeEmergencyActionAfterVerification({ type: apiType, code });
    } finally {
      setFlowBusy(false);
    }
  };

  const handlePasteTotp = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      const digits = String(raw || "").replace(/\D/g, "").slice(0, 6);
      if (digits.length === 6) setTotpCode(digits);
    } catch {
      // ignore
    }
  };

  const verificationUiOpen = verifyModal != null || chooseMethodsOpen;

  const anyModalOpen = addModalOpen || verificationUiOpen;

  useEffect(() => {
    if (!anyModalOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (verificationUiOpen) closeVerifyFlow();
      else closeAddModal();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [anyModalOpen, verificationUiOpen, closeVerifyFlow, closeAddModal]);

  const openAddModal = () => {
    if (listLoading) return;
    if (contacts.length >= EMERGENCY_CONTACT_MAX) {
      alertWarningMessage("You can add up to 5 emergency contacts only.");
      return;
    }
    resetAll();
    setAddModalOpen(true);
  };

  const openEditModal = (contact) => {
    const id = contact?._id != null ? String(contact._id) : "";
    if (!id) return;
    const { countryCode: cc, phoneDigits: pd } = splitInternationalMobile(contact.mobileNumber);
    setEditingContactId(id);
    setContactName(contact.fullName || "");
    setContactEmail(contact.emailId || "");
    setCountryCode(cc);
    setPhoneDigits(pd);
    resetVerificationUi();
    pendingFlowRef.current = null;
    setAddModalOpen(true);
  };

  const confirmRemoveContactToast = useCallback(
    () =>
      new Promise((resolve) => {
        toast.custom(
          (t) => (
            <div className="ec-toast-confirm" role="alertdialog" aria-labelledby="ec-toast-confirm-title">
              <p id="ec-toast-confirm-title" className="ec-toast-confirm__text">
                Remove this emergency contact?
              </p>
              <div className="ec-toast-confirm__actions">
                <button
                  type="button"
                  className="ec-toast-confirm__btn ec-toast-confirm__btn--ghost"
                  onClick={() => {
                    toast.dismiss(t.id);
                    resolve(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ec-toast-confirm__btn ec-toast-confirm__btn--danger"
                  onClick={() => {
                    toast.dismiss(t.id);
                    resolve(true);
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ),
          { id: "ec-emergency-remove-confirm", duration: Infinity, position: "top-center" }
        );
      }),
    []
  );

  const handleDeleteContact = async (contact) => {
    const id = contact?._id != null ? String(contact._id) : "";
    if (!id) return;
    const ok = await confirmRemoveContactToast();
    if (!ok) return;

    setDeleteBusyId(id);
    pendingFlowRef.current = {
      mode: "delete",
      reopenAddModalOnCancel: false,
      payload: { contactId: id },
    };
    resetVerificationUi();
    setResolvedMethods(null);

    await runEmergencyVerificationSequence();
  };

  const handleSaveMessage = async (contactId) => {
    const id = String(contactId);
    const message = messageDrafts[id] ?? "";

    setSaveMsgBusyId(id);
    pendingFlowRef.current = {
      mode: "saveMessage",
      reopenAddModalOnCancel: false,
      payload: { contactId: id, message },
    };
    resetVerificationUi();
    setResolvedMethods(null);

    await runEmergencyVerificationSequence();
  };

  const updateMessageDraft = (id, value) => {
    setMessageDrafts((prev) => ({ ...prev, [String(id)]: value }));
  };

  const showChooser = chooseMethodsOpen && verifyModal;

  return (
    <main className="ec-page" aria-labelledby="ec-title">
      <nav className="ec-page__crumbs" aria-label="Breadcrumb">
        <ol className="ec-page__crumbList">
          <li className="ec-page__crumbItem">
            <button type="button" className="ec-page__crumbLink" onClick={() => navigate("/user_profile/security")}>
              Security
            </button>
          </li>
          <li className="ec-page__crumbSep" aria-hidden="true">
            ›
          </li>
          <li className="ec-page__crumbItem ec-page__crumbItem--active" aria-current="page">
            Emergency Contact
          </li>
        </ol>
      </nav>

      <h1 id="ec-title" className="ec-page__title">
        Emergency Contact
      </h1>

      <div className="ec-page__art" aria-hidden="true">
        <img className="ec-page__artImg dark_img" src="/images/security/emergency_vector.svg" alt="" />
        <img className="ec-page__artImg light_img" src="/images/security/emergency_vector_light.svg" alt="" />
      </div>

      <p className="ec-page__body">
        At AGCX, your asset security is our highest priority. The Emergency Contact feature allows us to send email and SMS
        notifications to you or your selected contacts if your account stays inactive for a certain period. Your emergency contacts
        may also have the option to submit an inheritance request.
      </p>

      {/* <section className="ec-how" aria-label="How emergency contact works">
        <span className="ec-how__icon" aria-hidden="true">
          <i className="ri-question-line" />
        </span>
        <div className="ec-how__text">
          <div className="ec-how__title">How Does Emergency Contact Work?</div>
          <button type="button" className="ec-how__learn">
            Learn More
          </button>
        </div>
      </section> */}

      {(listLoading || contacts.length > 0) && (
        <section className="ec-list" aria-label="Your emergency contacts">
          {listLoading ? (
            <div className="ec-list__loading" role="status" aria-busy="true" aria-label="Loading contacts">
              <span className="spinner-border spinner-border-sm text-secondary" aria-hidden="true" />
            </div>
          ) : (
            <div className="ec-list__scroll">
              <ul className="ec-list__items">
                {contacts.map((c) => {
                  const cid = String(c._id);
                  return (
                    <li key={cid} className="ec-card">
                      <div className="ec-card__top">
                        <div className="ec-card__identity">
                          <span className="ec-card__name">{c.fullName || "—"}</span>
                          <span className="ec-card__meta">{c.emailId}</span>
                          <span className="ec-card__meta">{c.mobileNumber}</span>
                        </div>
                        <div className="ec-card__actions">
                          <button type="button" className="ec-card__btn ec-card__btn--ghost" onClick={() => openEditModal(c)}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="ec-card__btn ec-card__btn--danger"
                            disabled={deleteBusyId === cid}
                            onClick={() => void handleDeleteContact(c)}
                          >
                            {deleteBusyId === cid ? "Removing…" : "Remove"}
                          </button>
                        </div>
                      </div>
                      <label className="ec-card__msgLabel" htmlFor={`ec-msg-${cid}`}>
                        Message (optional)
                      </label>
                      <textarea
                        id={`ec-msg-${cid}`}
                        className="ec-card__textarea"
                        rows={3}
                        value={messageDrafts[cid] ?? ""}
                        onChange={(e) => updateMessageDraft(cid, e.target.value)}
                        placeholder="Notes or instructions for this contact"
                      />
                      <div className="ec-card__msgRow">
                        <button
                          type="button"
                          className="ec-card__btn ec-card__btn--primary"
                          disabled={saveMsgBusyId === cid}
                          onClick={() => void handleSaveMessage(cid)}
                        >
                          {saveMsgBusyId === cid ? "Saving…" : "Save message"}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </section>
      )}

      <button type="button" className="ec-page__cta" onClick={openAddModal}>
        Add Emergency Contact
      </button>

      {addModalOpen ? (
        <div className="ec-modal-overlay" role="presentation" onClick={closeAddModal}>
          <div
            className="ec-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ec-add-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="ec-modal__header">
              <button type="button" className="ec-modal__back" aria-label="Back" onClick={closeAddModal}>
                <i className="ri-arrow-left-line" aria-hidden="true" />
              </button>
            </header>

            <h2 id="ec-add-modal-title" className="ec-modal__title">
              {editingContactId ? "Edit Emergency Contact" : "Add Emergency Contact"}
            </h2>

            <form
              className="ec-modal__form"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void openEmergencyVerificationFromSave();
              }}
            >
              <section className="ec-modal__block" aria-labelledby="ec-modal-name-label">
                <h3 id="ec-modal-name-label" className="ec-modal__blockTitle">
                  Emergency Contact Name
                </h3>
                <input
                  id="ec-contact-name"
                  className="ec-modal__input"
                  type="text"
                  name="emergencyContactName"
                  autoComplete="name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  aria-describedby="ec-modal-name-help"
                />
                <p id="ec-modal-name-help" className="ec-modal__help">
                  Please enter a name that your emergency contact would recognize. We will use this name to contact them.
                </p>
              </section>

              <section className="ec-modal__block" aria-labelledby="ec-modal-contact-label">
                <h3 id="ec-modal-contact-label" className="ec-modal__blockTitle">
                  Contact Info
                </h3>

                <div className="ec-modal__contactStack">
                  <div className="ec-modal__row">
                    <label htmlFor="ec-contact-email" className="ec-modal__srOnly">
                      Email
                    </label>
                    <input
                      id="ec-contact-email"
                      className="ec-modal__input"
                      type="email"
                      name="emergencyContactEmail"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="Email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                    />
                  </div>

                  <div className="ec-modal__row">
                    <span id="ec-phone-label" className="ec-modal__srOnly">
                      Phone number
                    </span>
                    <div className="ec-modal__phone" aria-labelledby="ec-phone-label">
                      <AuthPhoneField
                        countryCode={countryCode}
                        onCountryChange={setCountryCode}
                        phone={phoneDigits}
                        onPhoneChange={(digits) => setPhoneDigits(digits.slice(0, 15))}
                        inputId="ec-emergency-country"
                        phonePlaceholder="Phone Number"
                      />
                    </div>
                  </div>
                </div>

                <p className="ec-modal__help">
                  Full name, email, and phone number are required. It would be preferable if the emergency contact is{" "}
                  <span className="ec-modal__helpAccent">also an AGCX user</span>.
                </p>
              </section>

              <footer className="ec-modal__footer">
                <button type="submit" className={`ec-modal__save${canSave ? " is-enabled" : ""}`} disabled={!canSave}>
                  Save
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {verifyModal === "passkey" && !showChooser ? (
        <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation">
          <div className="withdraw_passkey_modal" role="dialog" aria-modal="true" aria-label="Verify with passkey">
            <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={closeVerifyFlow}>
              ×
            </button>
            <div className="withdraw_passkey_title">Verify with passkey</div>
            <div className="withdraw_passkey_hero" aria-hidden="true">
              <img className="light_img" src="/images/passkey_vector_light.svg" alt="" />
              <img className="dark_img" src="/images/passkey_vector.svg" alt="" />
            </div>
            <div className="withdraw_passkey_text">Complete verification using your passkey</div>
            <div className="withdraw_passkey_subtext">Please follow the instructions on your browser to complete verification.</div>
            <button type="button" className="withdraw_passkey_verify" disabled={passkeyBusy} onClick={() => void verifyPasskeyEmergency({ silentCancel: false })}>
              {passkeyBusy ? "Verifying…" : "Verify"}
            </button>
            {alternateMethodKeys.length > 0 && passkeyAlternateEligible ? (
              <button type="button" className="withdraw_passkey_link" onClick={openChooseMethods}>
                Use another verification method
              </button>
            ) : null}
            <button type="button" className="withdraw_passkey_link" onClick={goSecurityUnavailable}>
              Security verification unavailable?
            </button>
            <div className="withdraw_passkey_footer">
              <img src="/images/protected_icon.svg" alt="shield icon" />
              <span>Protected by Balance Risk</span>
            </div>
          </div>
        </div>
      ) : null}

      {verifyModal === "totp" && !showChooser ? (
        <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
          <div className="withdraw_auth_modal" role="dialog" aria-modal="true" aria-label="Authenticator App Verification">
            <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={closeVerifyFlow}>
              ×
            </button>
            <div className="withdraw_auth_title">Authenticator App Verification</div>
            <div className="withdraw_auth_subtitle">
              Enter the 6-digit code generated by the
              <br />
              Authenticator App.
            </div>
            <div className="withdraw_auth_label">Authenticator App</div>
            <div className="withdraw_auth_input_row">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder=""
                value={totpCode}
                onChange={(e) => setTotpCode(String(e.target.value || "").replace(/\D/g, "").slice(0, 6))}
              />
              <button type="button" className="withdraw_auth_paste" onClick={() => void handlePasteTotp()}>
                Paste
              </button>
            </div>
            <button type="button" className="withdraw_auth_submit" disabled={flowBusy} onClick={() => void verifyTotpSubmit()}>
              {flowBusy ? "Verifying…" : "Submit"}
            </button>
            <div className="gapbtn_space">
              {alternateMethodKeys.length > 0 ? (
                <button type="button" className="withdraw_auth_link" onClick={openChooseMethods}>
                  Switch to Another Verification Method
                </button>
              ) : null}
              <button type="button" className="withdraw_auth_link" onClick={goSecurityUnavailable}>
                Security verification unavailable?
              </button>
            </div>
            <div className="withdraw_passkey_footer withdraw_auth_footer">
              <img src="/images/protected_icon.svg" alt="shield icon" />
              <span>Protected by Balance Risk</span>
            </div>
          </div>
        </div>
      ) : null}

      {(verifyModal === "email" || verifyModal === "mobile") && !showChooser ? (
        <div className="tf-sec-page__daev-overlay" role="presentation">
          <div
            className="tf-sec-page__daev-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ec-em-daev-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="tf-sec-page__daev-close" aria-label="Close" onClick={closeVerifyFlow}>
              ×
            </button>

            <h2 id="ec-em-daev-title" className="tf-sec-page__daev-title">
              {verifyModal === "mobile" ? "Mobile Verification" : "Email Verification"}
            </h2>
            <p className="tf-sec-page__daev-desc">
              {verifyModal === "mobile"
                ? `Tap Get Code to receive a 6-digit OTP at ${maskedPhone}, then enter it below.`
                : `Tap Get Code to receive a 6-digit OTP at ${maskedEmail}, then enter it below.`}
            </p>

            <label className="tf-sec-page__daev-label" htmlFor="ec-em-daev-code">
              Enter Verification Code
            </label>
            <div className="tf-sec-page__daev-inputWrap">
              <input
                id="ec-em-daev-code"
                className="tf-sec-page__daev-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder=""
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
              <button type="button" className="tf-sec-page__daev-getCode" disabled={otpSending} onClick={() => void sendEmergencyOtp(verifyModal)}>
                {otpSending ? "Sending…" : "Get Code"}
              </button>
            </div>

            <button
              type="button"
              className="tf-sec-page__daev-submit"
              disabled={otpCode.replace(/\D/g, "").length !== 6 || flowBusy}
              onClick={() => void verifyEmailOrMobileSubmit()}
            >
              {flowBusy ? "Verifying…" : "Submit"}
            </button>

            <div className="gapbtn_space">
              {alternateMethodKeys.length > 0 ? (
                <button type="button" className="tf-sec-page__daev-help" onClick={openChooseMethods}>
                  Switch verification method
                </button>
              ) : null}
              <button type="button" className="tf-sec-page__daev-help" onClick={goSecurityUnavailable}>
                Security verification unavailable?
              </button>
            </div>

            <div className="tf-sec-page__daev-footer">
              <img src="/images/protected_icon.svg" alt="shield icon" />
              <span>Protected by Balance Risk</span>
            </div>
          </div>
        </div>
      ) : null}

      {showChooser ? (
        <ChooseVerificationMethodModal
          methods={alternateMethodKeys}
          onClose={() => setChooseMethodsOpen(false)}
          onPick={(key) => void applyVerificationMethod(key)}
          onSecurityUnavailable={goSecurityUnavailable}
        />
      ) : null}
    </main>
  );
};

export default EmergencyContact;
